/* Eloss — facets (filtering + sorting)
 *
 * Spec: §E.11 (re-render through `?section_id=`, `history.replaceState`,
 * preserved scroll, polite live-region count), §P.5, §P.6.
 *
 * AN ENHANCEMENT, NOT THE FEATURE. sections/facets.liquid filters and sorts
 * natively with no script at all; this only replaces the page load with a
 * section fetch. If it fails or throws, the form still submits — which is why
 * the Apply button is hidden HERE, on connect, rather than in CSS.
 *
 * NOTHING IS FILTERED CLIENT-SIDE (rule 13). The product list is never read or
 * reordered: a URL is built from the form's own controls and whatever Shopify
 * returns is inserted verbatim.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* One persistent live region. It must OUTLIVE the swapped markup: a fresh
     aria-live node inserted with its text already set is not reliably
     announced. */
  let liveRegion = null;
  const announce = (text) => {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = text;
  };

  Eloss.define(
    'eloss-facets',
    class ElossFacets extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-facets-form]');
        this.sortForm = this.parentElement?.querySelector('[data-facets-sort]');
        this.grid = document.querySelector('[data-collection-grid]');
        if (!this.form) return;

        /* JS is running: the manual Apply button is now redundant. */
        this.classList.add('facets--enhanced');

        this.onChange = (event) => {
          if (event.target.matches('input, select')) this.schedule();
        };
        this.form.addEventListener('change', this.onChange);
        this.sortForm?.addEventListener('change', this.onChange);

        /* Intercept the no-JS submit so Enter in a price field cannot
           double-navigate mid-fetch. */
        this.onSubmit = (event) => {
          event.preventDefault();
          this.apply();
        };
        this.form.addEventListener('submit', this.onSubmit);
        this.sortForm?.addEventListener('submit', this.onSubmit);

        this.onOpen = (event) => {
          const t = event.target;
          if (t.closest('[data-facets-open]')) this.openPanel();
          else if (t.closest('[data-facets-close], [data-facets-overlay]')) this.closePanel();
        };
        this.addEventListener('click', this.onOpen);

        this.onKeydown = (event) => {
          if (!this.isOpen) return;
          if (event.key === 'Escape') {
            event.stopPropagation();
            this.closePanel();
          } else if (event.key === 'Tab') {
            this.trapFocus(event);
          }
        };
        /* Also on document: Escape must close the sheet even if focus is not
           inside it. */
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('change', this.onChange);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.sortForm?.removeEventListener('change', this.onChange);
        this.sortForm?.removeEventListener('submit', this.onSubmit);
        this.removeEventListener('click', this.onOpen);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('keydown', this.onKeydown);
        clearTimeout(this.timer);
        this.releaseBackground();
      }

      /* Coalesce keystrokes in the price fields; one code path for both. */
      schedule() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.apply(), 300);
      }

      /* Built from the form's own controls, never a hand-kept list. */
      buildUrl() {
        const url = new URL(this.form.action, window.location.origin);
        url.search = '';
        new FormData(this.form).forEach((value, key) => {
          if (String(value).trim() !== '') url.searchParams.append(key, value);
        });
        const sort = this.sortForm?.querySelector('[data-facets-sort-select]');
        if (sort && sort.value) url.searchParams.set('sort_by', sort.value);
        return url;
      }

      async apply() {
        const url = this.buildUrl();
        const active = document.activeElement;
        const key = active?.name ? `${active.name}|${active.value}` : null;

        const facetId = this.dataset.sectionId;
        const gridId = this.grid?.dataset.sectionId;
        if (!facetId || !gridId) return window.location.assign(url.toString());

        const ask = (id) => {
          const u = new URL(url);
          u.searchParams.set('section_id', id);
          return fetch(u.toString()).then((r) => (r.ok ? r.text() : Promise.reject(r.status)));
        };

        try {
          const [gridHtml, facetHtml] = await Promise.all([ask(gridId), ask(facetId)]);
          const parse = (h) => new DOMParser().parseFromString(h, 'text/html');

          const nextGrid = parse(gridHtml).querySelector('[data-collection-grid]');
          const gridHost = document.querySelector('[data-collection-grid]');
          if (nextGrid && gridHost) {
            gridHost.replaceWith(nextGrid);
            this.grid = nextGrid;
          }

          const nextFacets = parse(facetHtml).querySelector('[data-facets]');
          const nextSort = parse(facetHtml).querySelector('[data-facets-sort]');
          if (nextSort && this.sortForm) this.sortForm.replaceWith(nextSort);
          if (nextFacets) this.replaceWith(nextFacets);

          /* replaceState, not pushState (§E.11): filtering refines one view, so
             Back leaves the collection. Scroll is never touched. */
          window.history.replaceState({}, '', url.toString());

          const count = document.querySelector('[data-results-count]');
          if (count) announce(count.textContent.trim());

          Eloss.emit(Eloss.EVENTS.filterApplied, { url: url.toString() });

          /* Restore focus in the new markup — the phase 3 variant-picker bug. */
          if (key) {
            const restored = document
              .querySelector('[data-facets]')
              ?.querySelector(`[name="${CSS.escape(key.split('|')[0])}"]`);
            restored?.focus({ preventScroll: true });
          }
        } catch (e) {
          /* Any failure falls back to the navigation the form would have done. */
          window.location.assign(url.toString());
        }
      }

      get isOpen() {
        return this.classList.contains('facets--open');
      }

      openPanel() {
        this.opener = document.activeElement;
        this.classList.add('facets--open');
        this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
        this.querySelector('[data-facets-overlay]')?.removeAttribute('hidden');
        document.documentElement.style.overflow = 'hidden';
        void this.offsetWidth; /* flush the class before focusing */

        this.inerted = Array.from(document.body.children).filter(
          (el) => el !== this && !el.contains(this)
        );
        this.inerted.forEach((el) => { el.inert = true; el.setAttribute('aria-hidden', 'true'); });

        this.querySelector('[data-facets-heading]')?.focus();
      }

      closePanel() {
        if (!this.isOpen) return;
        this.classList.remove('facets--open');
        this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'false');
        this.querySelector('[data-facets-overlay]')?.setAttribute('hidden', '');
        document.documentElement.style.overflow = '';
        this.releaseBackground();

        const target =
          this.opener && this.opener !== document.body && document.contains(this.opener)
            ? this.opener
            : this.querySelector('[data-facets-open]');
        target?.focus();
        this.opener = null;
      }

      releaseBackground() {
        this.inerted?.forEach((el) => { el.inert = false; el.removeAttribute('aria-hidden'); });
        this.inerted = null;
      }

      trapFocus(event) {
        const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
})();
